import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Col,
  Dropdown,
  Empty,
  Input,
  MenuProps,
  Modal,
  Row,
  Select,
  Segmented,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FilterOutlined,
  MoreOutlined,
  PlusOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useFeedback } from '../utils/feedback';
import { authService } from '../services/auth';
import { getApiErrorMessage } from '../utils/apiError';
import { formatDate } from '../utils/dateUtils';
import { getCatalogProductDimensionParts } from '../utils/productDimensions';
import { getProductVariantDisplayName, isRealProductVariant } from '../utils/productSelectorLabel';
import { Product } from '../types/pz';
import {
  deleteProduct,
  fetchProductCategories,
  fetchProducts as fetchProductsApi,
  permanentlyDeleteProduct
} from '../modules/products/api';
import {
  canDeleteProductCatalogItem,
  canManageProductCatalog,
} from '../modules/products/productCatalogAccessPolicy';
import { ProductCategory, fetchProductCategoryTags } from '../modules/products/productCategoriesApi';
import ProductCategoriesTab from '../components/products/ProductCategoriesTab';
import '../components/layout/AppPage.css';
import './Products.css';

const { Option } = Select;
const { Text } = Typography;

interface ProductRecord extends Product {
  variantCount?: number | string;
}

interface ProductFilters {
  search: string;
  status: 'ACTIVE' | 'INACTIVE' | null;
  category: string | null;
  productCategoryId: string | null;
}

type ProductView = 'PRODUCTS' | 'HIERARCHIES';

interface ProductCategoryCellProps {
  name: string | null;
  color?: string | null;
  emptyLabel: React.ReactNode;
}

export const ProductCategoryCell: React.FC<ProductCategoryCellProps> = ({
  name,
  color,
  emptyLabel,
}) => {
  if (!name) return <Text type="secondary">{emptyLabel}</Text>;

  return (
    <Tooltip
      title={name}
      placement="topLeft"
      mouseEnterDelay={0.4}
      trigger={['hover', 'focus']}
    >
      <Tag
        className="products-category-tag"
        color={color || 'default'}
        tabIndex={0}
      >
        <span>{name}</span>
      </Tag>
    </Tooltip>
  );
};

const DEFAULT_PRODUCT_FILTERS: ProductFilters = {
  search: '',
  status: 'ACTIVE',
  category: null,
  productCategoryId: null,
};

const isProductActive = (product: ProductRecord) => product.isActive !== false;

export const getRealProductVariants = (product: ProductRecord) => (
  (product.variants || []).filter(isRealProductVariant)
);

const Products: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['products', 'common']);
  const feedback = useFeedback();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'categories' ? 'categories' : 'list';

  const [products, setProducts] = useState<ProductRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<ProductFilters>(DEFAULT_PRODUCT_FILTERS);
  const [categories, setCategories] = useState<string[]>([]);
  const [categoryTags, setCategoryTags] = useState<ProductCategory[]>([]);
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [productView, setProductView] = useState<ProductView>('PRODUCTS');
  const [pendingProductAction, setPendingProductAction] = useState<{
    product: ProductRecord;
    action: 'DEACTIVATE' | 'PERMANENT_DELETE';
  } | null>(null);
  const [productActionLoading, setProductActionLoading] = useState(false);

  const handleTabChange = (key: string) => {
    setSearchParams(key === 'categories' ? { tab: 'categories' } : {});
  };

  const currentUser = authService.getCurrentUser();
  const canEdit = canManageProductCatalog(currentUser);
  const canDelete = canDeleteProductCatalogItem(currentUser);

  const activeAdvancedFilterCount =
    (filters.status !== DEFAULT_PRODUCT_FILTERS.status ? 1 : 0) +
    (filters.category ? 1 : 0) +
    (filters.productCategoryId ? 1 : 0);
  const hasAnyFilter =
    filters.search !== DEFAULT_PRODUCT_FILTERS.search || activeAdvancedFilterCount > 0;

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.status, filters.category, filters.productCategoryId]);

  useEffect(() => {
    fetchCategories();
    loadCategoryTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const productData = await fetchProductsApi(filters);
      setProducts(productData);
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'products:error.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const categoryData = await fetchProductCategories();
      setCategories(categoryData);
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'products:error.fetchCategoriesFailed'));
    }
  };

  const loadCategoryTags = async () => {
    try {
      setCategoryTags(await fetchProductCategoryTags());
    } catch (error: any) {
      // Polish-first: show the translated message, not the backend English string.
      feedback.error(t('products:productCategory.loadFailed'));
    }
  };

  const handleProductCategoriesChanged = async () => {
    await Promise.all([loadCategoryTags(), fetchProducts()]);
  };

  const handleConfirmProductAction = async () => {
    if (!pendingProductAction) return;

    const productName = pendingProductAction.product.name;
    setProductActionLoading(true);
    try {
      if (pendingProductAction.action === 'PERMANENT_DELETE') {
        await permanentlyDeleteProduct(pendingProductAction.product.id);
        feedback.success(t('products:success.deleted', { name: productName }));
      } else {
        await deleteProduct(pendingProductAction.product.id);
        feedback.success(t('products:success.deactivated', { name: productName }));
      }
      setPendingProductAction(null);
      await fetchProducts();
    } catch (error: any) {
      if (error.response?.data?.code === 'PRODUCT_IN_USE') {
        feedback.error(t('products:error.productInUse', { name: productName }), { sticky: true });
      } else if (pendingProductAction.action === 'PERMANENT_DELETE') {
        feedback.error(t('products:error.deleteFailed', { name: productName }));
      } else {
        feedback.error(t('products:error.deactivateFailed', { name: productName }));
      }
    } finally {
      setProductActionLoading(false);
    }
  };

  const getStatusColor = (product: ProductRecord) =>
    isProductActive(product) ? 'success' : 'default';

  const getProductActionItems = (record: ProductRecord): MenuProps['items'] => [
    {
      key: 'view',
      icon: <EyeOutlined />,
      label: t('common:view'),
    },
    ...(canEdit ? [{
      key: 'edit',
      icon: <EditOutlined />,
      label: t('common:edit'),
    }] : []),
    ...(canDelete ? [{
      key: isProductActive(record) ? 'deactivate' : 'permanent-delete',
      danger: true,
      icon: <DeleteOutlined />,
      label: isProductActive(record)
        ? t('products:actions.deactivate')
        : t('products:actions.permanentDelete'),
    }] : []),
  ];

  const handleProductAction = (key: string, record: ProductRecord) => {
    if (key === 'view') {
      navigate(`/app/products/${record.id}`);
      return;
    }

    if (key === 'edit') {
      navigate(`/app/products/${record.id}?mode=edit`);
      return;
    }

    if (key === 'deactivate' || key === 'permanent-delete') {
      setPendingProductAction({
        product: record,
        action: key === 'permanent-delete' ? 'PERMANENT_DELETE' : 'DEACTIVATE',
      });
    }
  };

  const columns: ColumnsType<ProductRecord> = [
    {
      title: t('common:actions'),
      key: 'actions',
      width: 56,
      align: 'center',
      fixed: 'left',
      render: (_, record) => (
        <Dropdown
          menu={{
            items: getProductActionItems(record),
            onClick: ({ key, domEvent }) => {
              domEvent.stopPropagation();
              handleProductAction(key, record);
            },
          }}
          trigger={['click']}
        >
          <Button
            className="products-actions-button"
            icon={<MoreOutlined />}
            size="small"
            onClick={(event) => event.stopPropagation()}
            title={t('common:actions')}
          />
        </Dropdown>
      ),
    },
    {
      title: t('products:fields.name'),
      dataIndex: 'name',
      key: 'name',
      width: 340,
      render: (name: string, record) => {
        const classification = record.category
          ? t(`products:categories.${record.category}`, { defaultValue: record.category })
          : null;
        const meta = [classification, record.englishName].filter(Boolean).join(' · ');
        return (
          <div className="products-identity">
            <button className="products-name-button" onClick={() => navigate(`/app/products/${record.id}`)}>
              <span>{name}</span>
            </button>
            <ProductCategoryCell
              name={record.productCategoryName ?? null}
              color={record.productCategoryColor}
              emptyLabel={t('products:productCategory.none')}
            />
            {meta && <small className="products-identity__meta">{meta}</small>}
          </div>
        );
      },
    },
    {
      title: t('products:fields.dimensions'),
      key: 'dimensions',
      width: 118,
      render: (_, record) => {
        const parts = getCatalogProductDimensionParts(record);
        if (parts.length === 0) return '-';

        return (
          <dl className="products-dimensions">
            {parts.map((part) => (
              <div className="products-dimension" key={part.field}>
                <dt>{part.label}</dt>
                <dd>
                  <strong>{part.value}</strong>
                  <span className="products-dimensions-unit">mm</span>
                </dd>
              </div>
            ))}
          </dl>
        );
      },
    },
    {
      title: t('products:fields.status'),
      dataIndex: 'isActive',
      key: 'isActive',
      width: 132,
      render: (_: boolean, record) => (
        <Tag color={getStatusColor(record)}>
          {t(`products:status.${isProductActive(record) ? 'active' : 'inactive'}`)}
        </Tag>
      ),
    },
    {
      title: t('products:fields.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 144,
      render: (date: string) => formatDate(date),
    },
  ];

  const renderProductHierarchy = (product: ProductRecord) => {
    const variants = getRealProductVariants(product);

    return (
      <div className="products-hierarchy" aria-label={t('products:view.hierarchyFor', { name: product.name })}>
        {variants.map((variant) => (
          <div className="products-hierarchy-node products-hierarchy-node--variant" key={variant.id}>
            <span className="products-hierarchy-line" aria-hidden="true" />
            <div>
              <Text type="secondary">{t('products:view.variant')}</Text>
              <span className="products-hierarchy-variant-name">
                {getProductVariantDisplayName(variant, t('products:view.baseProduct'))}
              </span>
            </div>
            <Tag color={variant.isActive !== false ? 'success' : 'default'}>
              {t(`products:status.${variant.isActive !== false ? 'active' : 'inactive'}`)}
            </Tag>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="app-page products-page">
      <Tabs
        className="app-page__tabs"
        activeKey={activeTab}
        onChange={handleTabChange}
        items={[
          {
            key: 'list',
            label: t('products:title'),
            children: (
              <section className="app-page__card">
      <div className="app-page__toolbar">
        <Input
          className="app-page__toolbar-search"
          placeholder={t('products:filters.search')}
          prefix={<SearchOutlined />}
          value={filters.search}
          onChange={(event) => setFilters({ ...filters, search: event.target.value })}
          allowClear
        />
        <div className="app-page__toolbar-actions">
          <div className="app-page__toolbar-group">
            <Segmented
              aria-label={t('products:view.label')}
              value={productView}
              onChange={(value) => setProductView(value as ProductView)}
              options={[
                { label: t('products:view.products'), value: 'PRODUCTS' },
                { label: t('products:view.hierarchies'), value: 'HIERARCHIES' },
              ]}
            />
          </div>
          <Badge count={activeAdvancedFilterCount} size="small" offset={[-2, 2]}>
            <Button
              icon={<FilterOutlined />}
              type={advancedFiltersOpen || activeAdvancedFilterCount > 0 ? 'primary' : 'default'}
              ghost={advancedFiltersOpen || activeAdvancedFilterCount > 0}
              onClick={() => setAdvancedFiltersOpen((open) => !open)}
            >
              {t('products:filters.toggle')}
            </Button>
          </Badge>
          {hasAnyFilter && (
            <Button
              type="link"
              onClick={() => setFilters(DEFAULT_PRODUCT_FILTERS)}
            >
              {t('common:clearFilters')}
            </Button>
          )}
          {canEdit && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/app/products/new')}
            >
              {t('products:actions.create')}
            </Button>
          )}
        </div>
      </div>

      {productView === 'HIERARCHIES' && !loading && products.length > 0
        && products.every((product) => getRealProductVariants(product).length === 0) && (
        <Alert
          className="app-page__notice"
          type="info"
          showIcon
          message={t('products:view.noHierarchies')}
        />
      )}

      {advancedFiltersOpen && (
        <Row gutter={[12, 12]} className="app-page__filters">
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              aria-label={t('products:filters.status')}
              placeholder={t('products:filters.status')}
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value })}
              style={{ width: '100%' }}
            >
              <Option value="ACTIVE">{t('products:status.active')}</Option>
              <Option value="INACTIVE">{t('products:status.inactive')}</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              placeholder={t('products:filters.classification')}
              value={filters.category}
              onChange={(value) => setFilters({ ...filters, category: value })}
              allowClear
              style={{ width: '100%' }}
            >
              {categories.map(category => (
                <Option key={category} value={category}>
                  {t(`products:categories.${category}`, { defaultValue: category })}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              placeholder={t('products:filters.category')}
              value={filters.productCategoryId}
              onChange={(value) => setFilters({ ...filters, productCategoryId: value ?? null })}
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
              options={categoryTags.map((tag) => ({ value: tag.id, label: tag.name }))}
            />
          </Col>
        </Row>
      )}

      <Table
        className="products-table"
        dataSource={products}
        columns={columns}
        loading={loading}
        rowKey="id"
        size="middle"
        tableLayout="fixed"
        expandable={productView === 'HIERARCHIES' ? {
          expandedRowKeys: products
            .filter((product) => getRealProductVariants(product).length > 0)
            .map((product) => product.id),
          expandedRowRender: renderProductHierarchy,
          rowExpandable: (product) => getRealProductVariants(product).length > 0,
          showExpandColumn: false,
        } : undefined}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('products:empty')}
            />
          ),
        }}
        scroll={{ x: 820 }}
        pagination={{
          showSizeChanger: true,
          showTotal: (total, range) => t('common:pagination', { from: range[0], to: range[1], total }),
          pageSizeOptions: ['10', '25', '50', '100'],
          defaultPageSize: 25,
        }}
      />
              </section>
            ),
          },
          {
            key: 'categories',
            label: t('products:productCategory.tab'),
            children: (
              <section className="app-page__card">
                <ProductCategoriesTab canEdit={canEdit} onCategoriesChanged={handleProductCategoriesChanged} />
              </section>
            ),
          },
        ]}
      />
      <Modal
        title={pendingProductAction?.action === 'PERMANENT_DELETE'
          ? t('products:confirm.permanentDelete')
          : t('products:confirm.deactivate')}
        open={Boolean(pendingProductAction)}
        okText={pendingProductAction?.action === 'PERMANENT_DELETE'
          ? t('products:actions.permanentDelete')
          : t('products:actions.deactivate')}
        cancelText={t('common:no')}
        okButtonProps={{ danger: true }}
        confirmLoading={productActionLoading}
        onOk={handleConfirmProductAction}
        onCancel={() => {
          if (!productActionLoading) setPendingProductAction(null);
        }}
      />
    </div>
  );
};

export default Products;
